import { zodResolver } from '@hookform/resolvers/zod';
import type { DbaProvisionInput, DbaProvisionResult } from '@vivport/backend/types/dba';
import { AccountUsageIntent, Domain } from '@vivport/backend/types/enums';
import { useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { apiErrorMessage } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Notice } from '@/components/ui/notice';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useProvisionAccount } from '@/hooks/use-dba-mutations';

const domainValues = Object.values(Domain);
const usageIntentValues = [AccountUsageIntent.WORK, AccountUsageIntent.PERSONAL_AND_WORK] as const;

const optionalText = z
  .string()
  .trim()
  .transform((value) => (value === '' ? undefined : value));

const optionalNumber = z
  .string()
  .trim()
  .transform((value) => (value === '' ? undefined : Number(value)))
  .pipe(z.number().optional());

const formSchema = z
  .object({
    owner: z.object({
      firstName: z.string().trim().min(1, 'First name is required'),
      surname: z.string().trim().min(1, 'Surname is required'),
      email: z.string().trim().toLowerCase().email('Enter a valid email'),
      accountName: optionalText,
    }),
    hub: z.object({
      name: z.string().trim().min(1, 'Hub name is required'),
      domain: z.enum(domainValues),
      phone: optionalText,
      website: optionalText,
    }),
    usageIntent: z.enum(usageIntentValues),
    discountPercent: optionalNumber.pipe(z.number().int().min(1).max(100).optional()),
    complimentaryEnabled: z.boolean(),
    complimentaryCreditAud: z.string().trim(),
    complimentaryNote: z.string().trim(),
  })
  .superRefine((values, ctx) => {
    if (!values.complimentaryEnabled) return;
    const credit = Number(values.complimentaryCreditAud);
    if (!(credit > 0)) {
      ctx.addIssue({ code: 'custom', path: ['complimentaryCreditAud'], message: 'Credit must be greater than 0' });
    }
    if (!values.complimentaryNote) {
      ctx.addIssue({ code: 'custom', path: ['complimentaryNote'], message: 'A note is required' });
    }
  });

type FormInput = z.input<typeof formSchema>;
type FormOutput = z.output<typeof formSchema>;

function toProvisionInput(values: FormOutput): DbaProvisionInput {
  return {
    owner: values.owner,
    hub: values.hub,
    usageIntent: values.usageIntent,
    billing: {
      discountPercent: values.discountPercent,
      complimentary: values.complimentaryEnabled
        ? { creditAud: Number(values.complimentaryCreditAud), note: values.complimentaryNote }
        : undefined,
    },
  };
}

const defaultValues: FormInput = {
  owner: { firstName: '', surname: '', email: '', accountName: '' },
  hub: { name: '', domain: Domain.CONSTRUCTION, phone: '', website: '' },
  usageIntent: AccountUsageIntent.WORK,
  discountPercent: '',
  complimentaryEnabled: false,
  complimentaryCreditAud: '',
  complimentaryNote: '',
};

function FormField({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function ProvisionPage() {
  const provision = useProvisionAccount();
  const [result, setResult] = useState<DbaProvisionResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormInput, unknown, FormOutput>({ resolver: zodResolver(formSchema), defaultValues });
  const complimentaryEnabled = watch('complimentaryEnabled');

  const onSubmit = async (values: FormOutput) => {
    setSubmitError(null);
    try {
      const provisioned = await provision.mutateAsync(toProvisionInput(values));
      setResult(provisioned);
      reset(defaultValues);
    } catch (err) {
      setSubmitError(apiErrorMessage(err));
    }
  };

  return (
    <>
      <h1 className="text-xl font-medium">Provision account</h1>

      {result && (
        <Notice tone="success" className="space-y-1">
          <p>
            Provisioned account <span className="font-medium">{result.accountName}</span> with arrangement{' '}
            {result.arrangement}. An invite went to {result.inviteSentTo}.
          </p>
          <Link to={`/accounts/${result.accountId}`} className="underline underline-offset-4">
            Open account {result.accountName}
          </Link>
        </Notice>
      )}

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <section className="space-y-4">
              <h2 className="text-sm font-medium">Owner</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField id="owner-first" label="First name" error={errors.owner?.firstName?.message}>
                  <Input id="owner-first" {...register('owner.firstName')} />
                </FormField>
                <FormField id="owner-surname" label="Surname" error={errors.owner?.surname?.message}>
                  <Input id="owner-surname" {...register('owner.surname')} />
                </FormField>
                <FormField id="owner-email" label="Email" error={errors.owner?.email?.message}>
                  <Input id="owner-email" type="email" {...register('owner.email')} />
                </FormField>
                <FormField id="owner-account-name" label="Account name (optional)">
                  <Input id="owner-account-name" placeholder="Derived from the name" {...register('owner.accountName')} />
                </FormField>
              </div>
            </section>

            <section className="space-y-4 border-t pt-6">
              <h2 className="text-sm font-medium">Business hub</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField id="hub-name" label="Hub name" error={errors.hub?.name?.message}>
                  <Input id="hub-name" {...register('hub.name')} />
                </FormField>
                <FormField id="hub-domain" label="Domain">
                  <Controller
                    control={control}
                    name="hub.domain"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="hub-domain">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {domainValues.map((domain) => (
                            <SelectItem key={domain} value={domain}>
                              {domain}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
                <FormField id="hub-phone" label="Phone (optional)">
                  <Input id="hub-phone" {...register('hub.phone')} />
                </FormField>
                <FormField id="hub-website" label="Website (optional)">
                  <Input id="hub-website" {...register('hub.website')} />
                </FormField>
                <FormField id="usage-intent" label="Usage intent">
                  <Controller
                    control={control}
                    name="usageIntent"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="usage-intent">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {usageIntentValues.map((intent) => (
                            <SelectItem key={intent} value={intent}>
                              {intent}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
              </div>
            </section>

            <section className="space-y-4 border-t pt-6">
              <h2 className="text-sm font-medium">Billing</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="discount-percent"
                  label="Discount percent (optional)"
                  error={errors.discountPercent?.message}
                >
                  <Input id="discount-percent" type="number" min={1} max={100} step={1} {...register('discountPercent')} />
                </FormField>
              </div>
              <div className="flex items-center gap-3">
                <Controller
                  control={control}
                  name="complimentaryEnabled"
                  render={({ field }) => (
                    <Switch id="complimentary" checked={field.value} onCheckedChange={field.onChange} />
                  )}
                />
                <Label htmlFor="complimentary">Complimentary subscription with starting credit</Label>
              </div>
              {complimentaryEnabled && (
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    id="complimentary-credit"
                    label="Credit (AUD)"
                    error={errors.complimentaryCreditAud?.message}
                  >
                    <Input
                      id="complimentary-credit"
                      type="number"
                      min={0.01}
                      step={0.01}
                      {...register('complimentaryCreditAud')}
                    />
                  </FormField>
                  <FormField id="complimentary-note" label="Note" error={errors.complimentaryNote?.message}>
                    <Textarea id="complimentary-note" {...register('complimentaryNote')} />
                  </FormField>
                </div>
              )}
            </section>

            {submitError && <Notice tone="destructive">{submitError}</Notice>}

            <div className="flex justify-end border-t pt-6">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Provisioning…' : 'Provision account'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
